<script setup lang="ts">
import { computed, type PropType } from 'vue';
import {
  DEFAULT_CONTRACT_ICON_PALETTE,
  contractStateIconUrl,
  type ContractIconPalette,
  type ContractIconState,
} from './contractStateIcons';

const props = defineProps({
  /** The contract's own state (earliest of its contract files); drawn behind. */
  contract: {
    type: String as PropType<ContractIconState | null>,
    default: null,
  },
  /**
   * The amendments' state, drawn in front, offset up and right. Drawn as given —
   * `resolveContractIcon` is where a normal amendment is dropped.
   */
  amendment: {
    type: String as PropType<ContractIconState | null>,
    default: null,
  },
  palette: {
    type: Object as PropType<ContractIconPalette>,
    default: () => DEFAULT_CONTRACT_ICON_PALETTE,
  },
  /** Edge length of one tile in px. */
  size: {
    type: Number,
    default: 24,
  },
});

const contractUrl = computed(() =>
  props.contract ? contractStateIconUrl(props.contract, props.palette) : null
);
const amendmentUrl = computed(() =>
  props.amendment ? contractStateIconUrl(props.amendment, props.palette) : null
);

const stacked = computed(() => Boolean(contractUrl.value && amendmentUrl.value));
// The front tile sits 40% of a tile up and to the right of the back one.
const boxSize = computed(() => `${stacked.value ? Math.round(props.size * 1.4) : props.size}px`);
const tileSize = computed(() => `${props.size}px`);
</script>

<template>
  <!-- Two tiles overlap by an offset, which no flex/grid primitive expresses,
       so the stack is positioned by hand (see <style>). -->
  <span v-if="contractUrl || amendmentUrl" class="gaia-contract-state-icon">
    <img v-if="contractUrl" class="tile tile--back" :src="contractUrl" alt="" />
    <img v-if="amendmentUrl" class="tile tile--front" :src="amendmentUrl" alt="" />
  </span>
</template>

<style scoped>
.gaia-contract-state-icon {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  width: v-bind(boxSize);
  height: v-bind(boxSize);
}

.tile {
  position: absolute;
  display: block;
  width: v-bind(tileSize);
  height: v-bind(tileSize);
}

.tile--back {
  left: 0;
  bottom: 0;
}

.tile--front {
  right: 0;
  top: 0;
}
</style>
